/**
 * Footer - public site footer ("Data Product" v3, concept-matched).
 * Navy field: brand block (green tile + wordmark, mission line, green tagline),
 * three muted link columns, then a bottom bar with the copyright/disclaimer
 * and social icon buttons. No newsletter band, no accent stripe - calm.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Grid, Typography, Divider, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import { useThemeMode } from '../../context/ThemeContext';

const productLinks = [
  { label: 'Home', to: '/' },
  { label: 'Markets', to: '/markets' },
  { label: 'Commodities', to: '/commodities' },
  { label: 'Currency Analysis', to: '/insights' },
  { label: 'Sign In', to: '/login' },
];
const coverageItems = ['Kaduna State', 'Taraba State', 'Katsina State', 'Bauchi State'];
const companyLinks = [
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
  { label: 'API Documentation', to: '/developers' },
  { label: 'Privacy Policy', to: '/privacy' },
];

const colHeadSx = { fontWeight: 700, mb: 2.25, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.13em', fontSize: '0.7rem' };
const linkSx = { display: 'block', color: 'rgba(255,255,255,0.55)', textDecoration: 'none', mb: 1.5, fontSize: '0.92rem', fontWeight: 500, transition: 'color 0.2s', '&:hover': { color: '#fff' } };

function Footer() {
  const { palette } = useThemeMode();

  return (
    <Box component="footer" sx={{ bgcolor: palette.navy, color: '#fff' }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 8 }, pb: 3 }}>
        <Grid container spacing={{ xs: 5, md: 6 }}>
          {/* Brand */}
          <Grid item xs={12} md={4.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 2.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: '10px', background: 'linear-gradient(150deg, #1FA254, #22C55E)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon icon="ph:plant" width={20} height={20} color="#fff" />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', letterSpacing: '-0.02em', color: '#fff' }}>Farmgate</Typography>
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem', lineHeight: 1.65, mb: 2.5, maxWidth: 380 }}>
              Nigeria's commodity intelligence platform. Transparent local, global and currency data - decoded weekly for the people who trade it.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon icon="ph:chart-line-up" width={17} height={17} color={palette.accentLight} />
              <Typography sx={{ color: palette.accentLight, fontSize: '0.9rem', fontWeight: 600 }}>Nigeria's commodity markets, decoded.</Typography>
            </Box>
          </Grid>

          {/* Product */}
          <Grid item xs={6} sm={4} md={2.5}>
            <Typography sx={colHeadSx}>Product</Typography>
            {productLinks.map((l) => (
              <Typography key={l.to} component={Link} to={l.to} sx={linkSx}>
                {l.label}
              </Typography>
            ))}
          </Grid>

          {/* Coverage */}
          <Grid item xs={6} sm={4} md={2.5}>
            <Typography sx={colHeadSx}>Coverage</Typography>
            {coverageItems.map((item) => (
              <Typography key={item} sx={{ ...linkSx, '&:hover': {} }}>{item}</Typography>
            ))}
          </Grid>

          {/* Company */}
          <Grid item xs={6} sm={4} md={2.5}>
            <Typography sx={colHeadSx}>Company</Typography>
            {companyLinks.map((l) => (
              <Typography key={l.to} component={Link} to={l.to} sx={linkSx}>
                {l.label}
              </Typography>
            ))}
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mt: { xs: 5, md: 6 }, mb: 3 }} />

        {/* Bottom bar */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2.5, pb: 2 }}>
          <Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', lineHeight: 1.6, maxWidth: 640 }}>
              © 2026 Triple Gee Merchants Ltd. Farmgate is a market-information service; figures are indicative and not investment advice. Pilot coverage: Kaduna, Taraba, Katsina & Bauchi.
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', mt: 0.75 }}>Powered by Rahza Technology</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.25, flexShrink: 0 }}>
            {[
              { icon: 'ph:x-logo', label: 'Farmgate on X', href: '#' },
              { icon: 'ph:linkedin-logo', label: 'Farmgate on LinkedIn', href: '#' },
              { icon: 'ph:envelope-simple', label: 'Email Farmgate', href: 'mailto:data@farmgate.ng' },
            ].map((s) => (
              <IconButton
                key={s.icon}
                component="a"
                href={s.href}
                aria-label={s.label}
                sx={{
                  width: 40, height: 40, borderRadius: '10px',
                  color: 'rgba(255,255,255,0.6)',
                  bgcolor: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  transition: 'color 0.2s, border-color 0.2s, background-color 0.2s',
                  '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.28)', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <Icon icon={s.icon} width={18} height={18} />
              </IconButton>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
